import styles from 'css/src/components/share-video/share-video.module.scss';

import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';
import type { AspectRatio, OpaqueVideoData } from '@lumen5/luminary';
import Luminary, { OfflineAudioEncoder } from '@lumen5/luminary';
import type { SelectCallback } from '@restart/ui/types';

import { useFormFeedback } from '../auth/common/useFormFeedback';

import type { CommentablePersonProfile, PublicVideo, VideoVisibility } from 'js/src/client';
import {
    ApiService,
    ErrorEnum,
    EstimatedStatusEnum,
    SourceEnum,
    Urls,
    VideoStatus,
    VideoTitleDefaults,
    VideoTitleParams,
} from 'js/src/client';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import VideoCommentList from 'js/src/components/common/comments/VideoCommentList';
import { Toast } from 'js/src/components/common/Toast';
import {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_TOOLTIP_PLACEMENTS,
    BUTTON_VARIANTS,
    ICON_VARIANTS,
    PrimaryButton,
    SecondaryButton,
    SimpleButton,
    SPINNER_ICON,
    TertiaryButton,
} from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown, DROPDOWN_DIVIDER } from 'js/src/components/common/ui/Dropdown';
import Modal from 'js/src/components/common/ui/modal';
import { Tab, TAB_TITLE_POSITION, Tabs } from 'js/src/components/common/ui/Tabs';
import TextInput, {
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { UpgradeToRemoveCreditSceneCallout } from 'js/src/components/common/UpgradeToRemoveCreditSceneCallout';
import VideoPreview from 'js/src/components/common/video-preview/VideoPreview';
import useDownloadVideoPermissions, {
    approvalStatusDisabledButtonProps,
} from 'js/src/components/dashboard/hooks/useDownloadVideoPermissions';
import AutoResize from 'js/src/components/share-video/AutoResize';
import {
    handleDownloadErrors,
    useDownloadVideoByVideoStatus,
    useLoadRenderDetailsAndPoll,
    useVideoDownloader,
} from 'js/src/components/share-video/share-video-hooks';
import TranscriptModal from 'js/src/components/share-video/TranscriptModal';
import UserInformationDisplay from 'js/src/components/share-video/UserInformationDisplay';
import VideoVisibilitySelect from 'js/src/components/share-video/VideoVisibilitySelect';
import YouTubeChannelCallout from 'js/src/components/share-video/YouTubeChannelCallout';
import {
    canSeeVideoControls,
    convertUserToCommentablePersonProfile,
    getCanTranslateVideos,
    isVideoLimitExceededInCycle,
    loadTeamMembers,
    loadTeamSettings,
    selectUsersWithAccessToWorkspaceAndProject,
} from 'js/src/data/account';
import {
    getCommentsAreLoading,
    getNumOpenCommentsPerScene,
    getUsersFromCommentThread,
    loadVideoComments,
} from 'js/src/data/comments';
import {
    hideModal,
    isLoginRequiredShowing,
    LOCKED_FEATURES,
    MODAL_IDS,
    showModal,
} from 'js/src/data/modals';
import type { PublicVideoRenderEstimate, ShareVideoNotification } from 'js/src/data/share-video';
import {
    isVideoRendering,
    loadVideo,
    selectVideoProjectId,
    selectVideoVisibility,
    selectVideoWorkspaceId,
    updateVideoVisibility,
} from 'js/src/data/share-video';
import { setVideoName } from 'js/src/data/share-video/share-video-actions';
import { loadTemplates } from 'js/src/data/templates';
import { loadSupportedLanguages, loadVideoTranslations } from 'js/src/data/translations';
import type { ImmutableMap, NoOwnProps } from 'js/src/libs/consts';
import { ImmutableFromJS, roleCanCreateVideos, SHARE_PAGE_NOTIFICATION } from 'js/src/libs/consts';
import {
    copyTextToClipboard,
    getAbsoluteUrl,
    isEmailValid,
    pluralize,
} from 'js/src/libs/custom-utils';
import { trackVideoDownload } from 'js/src/libs/download-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { localStorageGet, LocalStorageKeys, localStorageSet } from 'js/src/libs/local-storage';
import Navigation from 'js/src/libs/navigation';
import QueryString from 'js/src/libs/querystring';
import human_readable_date from 'js/src/libs/readable-date';
import { type AppDispatch, type RootState, useAppDispatch } from 'js/src/libs/RootState';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import {
    bindLuminaryOperations,
    doesLuminaryContainAIAvatar,
    getVideoEditableMediaAssetSourceData,
    isVideoL5Branded,
    isVoiceDrivenSceneGroup,
    luminaryReduxConnect,
    slideIdsByStartTime,
    srtDownloadOperation,
    withDataLuminary,
} from 'js/src/luminary';
import { SOURCE_DISPLAY_NAME } from 'js/src/luminary/consts';
import { useLuminaryOperations, useLuminarySelector } from 'js/src/luminary/luminary-hooks';
import { downloadLuminaryFrameAsImgOperation } from 'js/src/luminary/luminary-operations';
import type { CacheRef, GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isApiErrorWithProperty } from 'js/src/types';
import JSZip from 'jszip';

enum TAB_KEYS {
    CREATE = 'create',
    COMMENT = 'comment',
}

type AddAnonymouseCommentCallback = ({
    external_user_name,
    external_user_email,
}: {
    external_user_name: string;
    external_user_email: string;
}) => void;

const AnonymousUserModal = ({
    showLoginFirstModal,
    hideAnonymousUserModal,
    addAnonymousComment,
}: {
    showLoginFirstModal: boolean;
    hideAnonymousUserModal: () => void;
    addAnonymousComment: AddAnonymouseCommentCallback;
}) => {
    const [anonymousEmail, setAnonymousEmail] = useState('');
    const [anonymousName, setAnonymousName] = useState('');
    const [anonymousUserValidated, setAnonymousUserValidated] = useState(false);
    const [validationFeedback, setValidationFeedback] = useFormFeedback(
        Immutable.Map({
            name: '',
            email: '',
        }),
    );

    const onEmailChange = (e: TypeInputChangeEvent) => {
        setAnonymousEmail(`${e.currentTarget.value}`.toLowerCase());
        anonymousEmail !== '' && setValidationFeedback({ type: 'CLEAR_ERRORS' });
    };

    const onEmailBlur = () => {
        if (!isEmailValid(anonymousEmail)) {
            setValidationFeedback({
                type: 'SET_ERROR',
                payload: Immutable.fromJS({
                    email: 'Please provide a valid email address.',
                }),
            });
        } else {
            setValidationFeedback({ type: 'CLEAR_ERRORS' });
        }
    };

    const onNameChange = (e: TypeInputChangeEvent) => {
        setAnonymousName(e.currentTarget.value);
        anonymousName !== '' && setValidationFeedback({ type: 'CLEAR_ERRORS' });
    };

    const onNameBlur = () => {
        if (!anonymousName || anonymousName === '') {
            setValidationFeedback({
                type: 'SET_ERROR',
                payload: Immutable.fromJS({
                    name: 'Please fill out your name',
                }),
            });
        } else {
            setValidationFeedback({ type: 'CLEAR_ERRORS' });
        }
    };

    const handleFormFilled = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();

        addAnonymousComment({
            external_user_name: anonymousName, // TODO: defaults, not sure if this is good idea
            external_user_email: anonymousEmail, // TODO: defaults, not sure if this is good idea
        });

        // save in localStorage
        localStorageSet(LocalStorageKeys.COMMENTER_USERNAME, anonymousName);
        localStorageSet(LocalStorageKeys.COMMENTER_EMAIL, anonymousEmail);

        hideAnonymousUserModal();
    };

    useEffect(() => {
        if (anonymousName !== '' && isEmailValid(anonymousEmail)) {
            setAnonymousUserValidated(true);
        }
    }, [anonymousName, anonymousEmail]);

    return (
        <Modal
            showing={showLoginFirstModal}
            title="Want to leave a comment?"
            backdrop="static"
            onHidden={hideAnonymousUserModal}
        >
            <Callout
                msgType={CALLOUT_TYPES.INFO}
                text="Please leave your information to post a comment. You only need to do this once."
            />

            <TextInput
                variant={TEXT_INPUT_VARIANTS.STANDARD}
                className="rounded mb-l5-16"
                text={anonymousName}
                placeholder="Name"
                onChange={onNameChange}
                onBlur={onNameBlur}
                formFeedbackMessage={validationFeedback.get('name')}
            />

            <TextInput
                variant={TEXT_INPUT_VARIANTS.STANDARD}
                className="rounded mb-l5-16"
                text={anonymousEmail}
                placeholder="Email"
                onChange={onEmailChange}
                onBlur={onEmailBlur}
                formFeedbackMessage={validationFeedback.get('email')}
            />

            <p className="mb-l5-24">
                By providing an email, you will get notified when anyone mentions you in a comment.
            </p>
            <div className="modal-button-footer">
                <p className="me-l5-auto">
                    Use Lumen5?{' '}
                    <a href={`${Urls.LOGIN}?${AuthQueryParams.NEXT}=${window.location.pathname}`}>
                        Log in
                    </a>
                </p>

                <TertiaryButton onClick={hideAnonymousUserModal} text="Cancel" />
                <PrimaryButton
                    disabled={!anonymousUserValidated}
                    onClick={handleFormFilled}
                    text="Continue"
                />
            </div>
        </Modal>
    );
};

type ShareVideoPresentationOwnProps = {
    video: ImmutableMap<PublicVideo>;
    loading: boolean;
    visibility: VideoVisibility;
    renderEstimate: ImmutableMap<PublicVideoRenderEstimate> | null;
    isVideoRendering: boolean;
    updateVideoVisibility: (visibility: VideoVisibility) => void;
    updateVideoName: (newName: string) => void;
    showControls: boolean;
    aspectRatio: AspectRatio;
    currentSceneIdForComments: string | null;
    sceneList: { id: string; startTime: number }[];
    numOpenCommentsPerScene?: Immutable.Map<string, number>;
    commentMarkerCallback: (startTime: number, forceCommentingMode?: boolean) => void;
    mentionableUsers: Immutable.List<ImmutableMap<CommentablePersonProfile>>;
    videoOwner: number;
    showLoginFirstModal: boolean;
    showAnonymousUserModal: VoidFunction;
    hideAnonymousUserModal: VoidFunction;
    isVideoL5Branded: boolean;
    canDownload: boolean;
    onCreateNewVideo: VoidFunction;
    videoHasVoiceDrivenSceneGroups: boolean;
    srtDownload: (videoName: string) => Promise<void>;
    mediaSourceInfo: [number, string][];
    showTranslationUpsellModal: VoidFunction;
};

const ShareVideoPresentation = React.memo(function ShareVideoPresentation({
    video,
    loading,
    visibility,
    renderEstimate,
    isVideoRendering,
    updateVideoVisibility,
    updateVideoName,
    showControls,
    aspectRatio,
    currentSceneIdForComments,
    sceneList,
    numOpenCommentsPerScene = Immutable.Map<string, number>(),
    commentMarkerCallback,
    mentionableUsers,
    videoOwner,
    showLoginFirstModal,
    showAnonymousUserModal,
    hideAnonymousUserModal,
    isVideoL5Branded,
    canDownload,
    onCreateNewVideo,
    videoHasVoiceDrivenSceneGroups,
    srtDownload,
    mediaSourceInfo,
    showTranslationUpsellModal,
}: ShareVideoPresentationOwnProps) {
    const videoId = video.get('id');
    const dispatch = useAppDispatch();
    const [videoDownloadUrl, setVideoDownloadUrl] = useState<null | string>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [isShareFetching, setIsShareFetching] = useState(false);

    // Starts the download if requested
    const downloadTag = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
    // Prevents multiple concurrent share operations
    const isShareInProgress = useRef(false);

    const videoRenders = video.get('video_renders');
    const videoStatus = video.get('status');

    const [notifications, setNotifications] = useState<ShareVideoNotification[]>([]);
    const [suspendedCommentCallback, setSuspendedCommentCallback] =
        useState<AddAnonymouseCommentCallback | null>(null);

    const [forceVideoToPause, setForceVideoToPause] = useState(false);

    const [showingTranscriptModal, setShowingTranscriptModal] = useState(false);

    const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
    const isWebShareSupported =
        typeof navigator !== 'undefined' && typeof navigator.share !== 'undefined';

    const containsAIAvatar = useLuminarySelector(doesLuminaryContainAIAvatar, []);

    const canTranslateVideos = useSelector(getCanTranslateVideos);

    const sendVideoTitleExceedsCharLimitEvent = (text_num_characters: number) => {
        analytics.track('Video Title Exceeds Limit', {
            video_id: video.get('id'),
            character_limit: VideoTitleParams.MAX_LENGTH,
            number_of_characters: text_num_characters,
        });
    };

    const [activeTabKey, setActiveTabKey] = React.useState(TAB_KEYS.COMMENT);

    const toggleTranscriptModal = useCallback(() => {
        if (!showingTranscriptModal) {
            analytics.track('Share page view transcript clicked', {
                video_id: videoId,
            });
        }
        setShowingTranscriptModal(!showingTranscriptModal);
    }, [showingTranscriptModal, videoId]);

    useEffect(() => {
        const defaultActiveTabKey = showControls ? TAB_KEYS.CREATE : TAB_KEYS.COMMENT;
        setActiveTabKey(defaultActiveTabKey);
    }, [showControls]);

    const onCommentMarkerCallback = React.useCallback(
        (startTime: number) => {
            commentMarkerCallback(startTime);
        },
        [commentMarkerCallback],
    );
    const onTabSelect = React.useCallback(
        (eventKey: TAB_KEYS, e: React.SyntheticEvent<unknown>) => {
            setActiveTabKey(eventKey);
        },
        [],
    ) as SelectCallback;

    let disabledProps: Partial<ComponentProps<typeof PrimaryButton>> = {};
    const estimatedStatus = renderEstimate?.get('estimated_status');
    if (isVideoRendering || isFetching || !canDownload) {
        // if download is disabled due to approvals, show helpful tooltip
        if (!canDownload) {
            disabledProps = {
                ...approvalStatusDisabledButtonProps,
                tooltipPlacement: BUTTON_TOOLTIP_PLACEMENTS.BOTTOM,
            };
        }

        // otherwise, it'll a spinner and is disabled
        else {
            disabledProps = {
                disabled: true,
            };
        }
    } else if (
        videoStatus === VideoStatus.FAILED ||
        (estimatedStatus !== undefined &&
            [EstimatedStatusEnum.RETRYING, EstimatedStatusEnum.ERROR].includes(estimatedStatus))
    ) {
        disabledProps = {
            disabled: true,
        };
    }
    const downloadDisabled = disabledProps['disabled'] === true;
    // start the download process
    const videoDownloader = useVideoDownloader(videoId, setIsFetching, setVideoDownloadUrl);
    useDownloadVideoByVideoStatus(
        videoDownloader.current,
        videoDownloadUrl,
        downloadTag.current,
        downloadDisabled,
    );

    const getSceneIndexById = useCallback(
        (sceneId: string | null) => sceneList.findIndex((slide) => slide.id === sceneId) + 1,
        [sceneList],
    );

    const fullVideoLink = getAbsoluteUrl(video.get('share_link'));
    const copyLinkClicked = useCallback(() => {
        analytics.track('Share page copy link clicked', {
            video_id: videoId,
        });

        copyTextToClipboard(fullVideoLink)
            .then(() => {
                Toast.success('Link copied!');
            })
            .catch(() => {
                Toast.error(
                    'We are currently unable to copy link automatically, please copy the link from the browser URL bar.',
                );
            });
    }, [fullVideoLink, videoId]);

    const editVideoClicked = useCallback(() => {
        analytics.track('Share page edit video clicked', {
            video_id: videoId,
        });
    }, [videoId]);

    const downloadButtonClicked = useCallback(() => {
        const doDownload = async () => {
            // If the downloadUrl is set, the click on the button will trigger the download.
            // In that case we just want to call the track endpoint.
            // The track endpoint may raise an error if the user can't download the video for some reason.
            const videoId = video.get('id');
            try {
                await trackVideoDownload(videoId);
            } catch (err) {
                // handle the case where the video hasn't been published yet – don't show an error but start the process
                if (
                    isApiErrorWithProperty(err, 'error') &&
                    err.body.error === ErrorEnum.NOT_PUBLISHED
                ) {
                    if (!videoDownloader.current) {
                        throw new Error('Video downloader not initialized');
                    }
                    videoDownloader.current.download();
                } else {
                    // we only want to show errors if the user can't actually download from the url
                    if (videoDownloadUrl === null) {
                        handleDownloadErrors(err, dispatch);
                    }
                }
            }
        };

        void doDownload();
    }, [dispatch, video, videoDownloadUrl, videoDownloader]);

    const [downloadingScenes, setDownloadingScenes] = React.useState(false);
    const [downloadingAudio, setDownloadingAudio] = React.useState(false);

    const downloadAllScenesZip = useCallback(async () => {
        setDownloadingScenes(true);
        try {
            const zip = new JSZip();
            const videoData = video.get('video_data').toJS();

            const luminary = new Luminary({
                fetchResources: {
                    audio: false,
                    media: true,
                    transitions: false,
                    fonts: true,
                },
                disableRenderer: false,
                resolutionValue: 512,
            });

            luminary.load(videoData);
            await luminary.onCanPlay();

            let sceneIndex = 0;
            for (const scene of luminary.scenes) {
                const canvas = document.createElement('canvas');
                const { width, height } = luminary.outputResolution;
                canvas.width = width;
                canvas.height = height;

                try {
                    await scene.renderPreviewToCanvas(canvas, {
                        subsceneIndex: 0,
                    });
                } catch (e) {
                    Toast.error(`Failed to render scene ${sceneIndex + 1}`);
                    continue;
                }

                const blob: Blob = await new Promise((resolve, reject) => {
                    canvas.toBlob((blobResult) => {
                        if (!blobResult) {
                            reject(new Error(`Failed to create blob for scene ${scene.id}`));
                        } else {
                            resolve(blobResult);
                        }
                    }, 'image/png');
                });
                zip.file(`scene_${++sceneIndex}.png`, blob);
            }

            const content = await zip.generateAsync({
                type: 'blob',
            });
            const url = URL.createObjectURL(content);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${video.get('name')}_scenes.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            analytics.track('ZIP Previews Button Clicked');
        } catch (err) {
            Toast.error('Download failed.');
            ErrorReporter.capture(err);
            analytics.track('ZIP Previews Button Failed');
        } finally {
            setDownloadingScenes(false);
        }
    }, [video, setDownloadingScenes]);

    const downloadAudioWav = useCallback(async () => {
        setDownloadingAudio(true);
        try {
            const videoData = video.get('video_data').toJS();
            const videoName = video.get('name') || 'video';

            const duration = await withDataLuminary(
                async (dataLuminary) => {
                    dataLuminary.load(videoData);
                    await dataLuminary.onCanPlay();
                    return dataLuminary.duration;
                },
                {
                    luminarySettings: {
                        fetchResources: {
                            audio: true,
                            media: false,
                            transitions: false,
                            fonts: false,
                        },
                    },
                },
            );

            if (!duration || duration <= 0) {
                throw new Error('Video has no audio or invalid duration');
            }

            const encoder = new OfflineAudioEncoder();
            encoder.setBasename(`${videoName}_audio`);
            encoder.setStartTime(0);
            encoder.setStopTime(duration);

            encoder.luminary.load(videoData);
            await encoder.encode();
            encoder.download();

            Toast.success('Audio download started!');
            analytics.track('Audio WAV Download Button Clicked');
        } catch (err) {
            Toast.error('Audio download failed.');
            ErrorReporter.capture(err);
            analytics.track('Audio WAV Download Button Failed');
        } finally {
            setDownloadingAudio(false);
        }
    }, [video, setDownloadingAudio]);

    const { downloadLuminaryFrameAsImg } = useLuminaryOperations({
        downloadLuminaryFrameAsImgOperation,
    });
    const [downloadingFrame, setDownloadingFrame] = useState(false);
    const videoData = video.get('video_data');
    const videoName = video.get('name');

    const downloadCurrentFrame = useCallback(async () => {
        setDownloadingFrame(true);
        try {
            await downloadLuminaryFrameAsImg(previewCurrentTime, videoName, videoData.toJS());
            analytics.track('Video frame saved', { timeMillis: previewCurrentTime });
        } catch (err) {
            Toast.error('Frame download failed.');
            ErrorReporter.capture(err);
        } finally {
            setDownloadingFrame(false);
        }
    }, [downloadLuminaryFrameAsImg, videoData, videoName, previewCurrentTime]);

    const handleWebShare = useCallback(async () => {
        if (!videoDownloadUrl) {
            // This should never happen since button only renders when videoDownloadUrl is truthy
            ErrorReporter.capture('handleWebShare called without videoDownloadUrl');
            return;
        }

        if (isShareInProgress.current) {
            return;
        }

        let fileSizeMB: number | undefined;
        try {
            isShareInProgress.current = true;
            setIsShareFetching(true);

            const response = await fetch(videoDownloadUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch video');
            }

            const blob = await response.blob();
            const videoName = video.get('name');
            const file = new File([blob], `${videoName}.mp4`, { type: 'video/mp4' });
            fileSizeMB = blob.size / (1024 * 1024);

            if (!navigator.canShare({ files: [file] })) {
                Toast.error('File sharing is not supported on this browser.');
                setIsShareFetching(false);
                return;
            }

            setIsShareFetching(false);

            await navigator.share({
                files: [file],
                title: videoName,
            });

            analytics.track('Web Share Completed', {
                video_id: video.get('id'),
                file_size_mb: fileSizeMB,
            });
        } catch (err) {
            setIsShareFetching(false);

            if (err instanceof Error && err.name === 'AbortError') {
                // User cancelled the share - no error to show
                return;
            }

            analytics.track('Web Share Failed', {
                video_id: video.get('id'),
                ...(fileSizeMB !== undefined && { file_size_mb: fileSizeMB }),
            });

            Toast.error('Failed to share. Please try again.');
            ErrorReporter.capture(err);
        } finally {
            isShareInProgress.current = false;
        }
    }, [video, videoDownloadUrl]);

    const frameTimestamp = getTimeStringFromSeconds(previewCurrentTime / 1000);
    const isAnyDownloadInProgress = downloadingScenes || downloadingAudio || downloadingFrame;

    const downloadDropdownItems = React.useMemo(() => {
        if (!showControls) {
            return Immutable.List<DropdownItemProp<{ action: string }>>();
        }

        const items: Array<{
            id: string;
            text?: string;
            onSelectData?: { action: string };
        }> = [
            { id: 'video', text: 'Video (.mp4)', onSelectData: { action: 'video' } },
            { id: DROPDOWN_DIVIDER },
            { id: 'scenes', text: 'Scene previews (.zip)', onSelectData: { action: 'scenes' } },
            { id: 'audio', text: 'Audio (.wav)', onSelectData: { action: 'audio' } },
        ];

        if (videoStatus === VideoStatus.PUBLISHED) {
            items.push({
                id: 'frame',
                text: `Current frame (${frameTimestamp}) (.png)`,
                onSelectData: { action: 'frame' },
            });
        }

        // Only show for published videos because we want to help ensure for the user
        // that the downloaded SRT will match the downloaded video.
        if (videoStatus === VideoStatus.PUBLISHED && videoHasVoiceDrivenSceneGroups) {
            items.push({
                id: 'captions',
                text: 'Captions (.srt)',
                onSelectData: { action: 'captions' },
            });
        }

        return ImmutableFromJS(items) as Immutable.List<DropdownItemProp<{ action: string }>>;
    }, [videoStatus, showControls, frameTimestamp, videoHasVoiceDrivenSceneGroups]);

    const onDownloadDropdownSelect = useCallback(
        (onSelectData: ImmutableMap<{ action: string }>) => {
            switch (onSelectData.get('action')) {
                case 'video':
                    downloadTag.current?.click();
                    break;
                case 'scenes':
                    downloadAllScenesZip();
                    break;
                case 'audio':
                    downloadAudioWav();
                    break;
                case 'frame':
                    downloadCurrentFrame();
                    break;
                case 'captions':
                    if (SERVER.USER) {
                        analytics.setVideoIdContext(video.get('id'));
                        analytics.track('SRT Download Button Clicked', {
                            userEmail: SERVER.USER.email,
                        });
                    }
                    void srtDownload(video.get('name'));
                    break;
                default:
                    throw new Error(`Unknown download action: ${onSelectData.get('action')}`);
            }
        },
        [downloadAllScenesZip, downloadAudioWav, downloadCurrentFrame, video, srtDownload],
    );

    const downloadDropdownToggleProps = React.useMemo(
        () =>
            ImmutableFromJS({
                text: 'Download...',
                iconLeft: isAnyDownloadInProgress ? SPINNER_ICON : 'download',
                iconRight: 'arrow_drop_down',
                variant: BUTTON_VARIANTS.LINK,
                color: BUTTON_COLORS.SIMPLE,
                disabled: isAnyDownloadInProgress,
            }) as unknown as Immutable.Collection<unknown, unknown>,
        [isAnyDownloadInProgress],
    );

    if (loading) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                <h4 className="text-muted">Loading...</h4>
            </div>
        );
    }

    const lastUpdatedDate = human_readable_date(video.get('last_updated_date'));

    const sortedVideoSourceList =
        videoRenders.size > 1
            ? videoRenders.sortBy((segment) => segment.get('series_index'))
            : videoRenders;
    const videoPreviewNode = (
        <VideoPreview
            enableKeyboardShortcuts={true}
            startMuted={false}
            showMuteIndicator={true}
            autoplay={false}
            commentMarkerCallback={onCommentMarkerCallback}
            numOpenCommentsPerScene={numOpenCommentsPerScene}
            shouldVideoPause={forceVideoToPause}
            onPlay={() => setForceVideoToPause(false)}
            sortedVideoSourceList={sortedVideoSourceList}
            isHtmlVideo={videoStatus === VideoStatus.PUBLISHED}
            onCurrentTimeUpdate={setPreviewCurrentTime}
            enableComments
        />
    );

    const addNotification = (notification: ShareVideoNotification) => {
        setNotifications([notification, ...notifications]);
    };

    const translateVideoClicked = () => {
        if (!canTranslateVideos) {
            showTranslationUpsellModal();
            return;
        }
        ApiService.videosDuplicateVideosCreate({
            body: {
                video_ids: [video.get('id')],
                with_prefix: true,
            },
        })
            .then((response) => {
                const queryParams = new URLSearchParams({
                    id: response.data[0].id.toString(),
                    showTranslationModal: '1',
                });
                Navigation.goToLocation(Urls.APP + '?' + queryParams.toString());
            })
            .catch((e: unknown) => {
                Toast.error(
                    'Something went wrong when trying to translate your video, please try again.',
                );
            });
    };

    const addAnonymousComment: AddAnonymouseCommentCallback = ({
        external_user_name = localStorageGet(LocalStorageKeys.COMMENTER_USERNAME),
        external_user_email = localStorageGet(LocalStorageKeys.COMMENTER_EMAIL),
    }) => {
        if (suspendedCommentCallback && typeof suspendedCommentCallback === 'function') {
            if (external_user_name === null || external_user_email === null) {
                throw new Error('External user name and email must be set');
            }
            suspendedCommentCallback({
                external_user_name,
                external_user_email,
            });
            setSuspendedCommentCallback(null);
        }
    };

    const handleOnBeforeCommenting = (addCommentCallback: AddAnonymouseCommentCallback) => {
        const stored_name = localStorageGet(LocalStorageKeys.COMMENTER_USERNAME);
        const stored_email = localStorageGet(LocalStorageKeys.COMMENTER_EMAIL);
        if (!stored_name || !stored_email) {
            setSuspendedCommentCallback(() => addCommentCallback); // you can save a function in state, but needs to be wrapped
            showAnonymousUserModal();
        }
        // saved anonymous user, let them comment with saved name/email
        else {
            addCommentCallback({
                external_user_name: stored_name,
                external_user_email: stored_email,
            });
        }
    };

    let editButton = null;
    let visibilitySelect = null;
    let downloadButton = null;
    let mediaSourcesDetails = null;
    const copyLinkButtonClass = classNames({
        'ms-l5-8': showControls,
        'ps-l5-0': !showControls,
    });

    // Populate the elements only the owner of the video will see
    if (showControls) {
        visibilitySelect = (
            <VideoVisibilitySelect
                visibility={visibility}
                updateVideoVisibility={updateVideoVisibility}
            />
        );

        const isVideoEditable = !(isVideoRendering || videoStatus === VideoStatus.FAILED);
        const editUrl = Urls.APP + '?id=' + video.get('id');

        // We don't want repeated attempts to render a failed or in-progress render
        if (isVideoEditable) {
            editButton = (
                <SecondaryButton
                    href={editUrl}
                    text="Edit video"
                    className="mx-l5-4"
                    onClick={editVideoClicked}
                />
            );
        }

        downloadButton = (
            <PrimaryButton
                ref={downloadTag}
                onClick={downloadButtonClicked}
                text="Download video"
                iconLeft={isVideoRendering || isFetching ? SPINNER_ICON : ''}
                href={videoDownloadUrl ?? undefined}
                className="mx-l5-4"
                {...disabledProps}
            />
        );

        if (SERVER?.USER?.team_id === 1578375) {
            // only show for Bloomberg as the requesting team
            // they need the media source & source id info for each editable media asset in the video
            const ignore_sources = [
                SourceEnum.SOURCE_USER,
                SourceEnum.SOURCE_USER_WATERMARK,
                SourceEnum.SOURCE_USER_OUTRO,
                SourceEnum.SOURCE_ARTICLE,
                SourceEnum.SOURCE_LUMEN5_COLOURS,
                SourceEnum.SOURCE_LUMEN5_ASSETS,
                SourceEnum.SOURCE_LUMEN5_ICONS_PREMIUM,
            ];
            const mediaSourcesDetailsNodes = mediaSourceInfo
                .filter((info) => ignore_sources.indexOf(info[0]) === -1)
                .map((sourceInfo) => {
                    // @ts-expect-error Element implicitly has an 'any'
                    const library_display_name = SOURCE_DISPLAY_NAME[sourceInfo[0]];
                    return (
                        <div key={`${sourceInfo[0]}_${sourceInfo[1]}`}>
                            {sourceInfo[1]} (library: {library_display_name})
                        </div>
                    );
                });
            mediaSourcesDetails = (
                <Callout
                    className="mx-l5-16"
                    msgType={CALLOUT_TYPES.INFO}
                    title="Media used in video"
                    text={mediaSourcesDetailsNodes}
                />
            );
        }
    }

    let renderNotification = null;
    if (isVideoRendering || videoStatus === VideoStatus.FAILED) {
        if (renderEstimate) {
            if (renderEstimate.get('estimated_status') === EstimatedStatusEnum.UNKNOWN) {
                const estimatedTimeRemaining = renderEstimate.get('estimated_time_remaining');
                const estimateMessage =
                    estimatedTimeRemaining >= 1
                        ? 'start automatically in about ' +
                          estimatedTimeRemaining.toString() +
                          ` ${pluralize(estimatedTimeRemaining, 'minute')}.`
                        : 'soon start automatically.';
                const watermarkMessage = containsAIAvatar
                    ? 'Your avatar will be synced with your script and stock media watermarks will be removed when your video is finished rendering.'
                    : 'Stock media watermarks will be removed when your video is finished rendering.';
                renderNotification = (
                    <Callout
                        title="Your video is now rendering!"
                        text={
                            <>
                                {watermarkMessage} Your download will {estimateMessage}
                            </>
                        }
                    />
                );
            } else if (renderEstimate.get('estimated_status') === EstimatedStatusEnum.RETRYING) {
                renderNotification = (
                    <Callout text="We're still working on it - check back in about an hour." />
                );
            } else if (renderEstimate.get('estimated_status') === EstimatedStatusEnum.ERROR) {
                renderNotification = (
                    <Callout
                        text="Something went wrong publishing your video, our team will fix within 24 hours."
                        ctaText="Learn more"
                        onCtaClick={() =>
                            window.open(
                                'https://help.lumen5.com/en/article/something-went-wrong-with-my-video-rendering-1is3mom/',
                                '_blank',
                            )
                        }
                    />
                );
            }
        }
    }

    let notificationNodes = null;
    if (notifications.length) {
        notificationNodes = notifications.map((notification, index) => {
            const isError = notification.type === 'error';
            let actionLinkNode;

            if (isError) {
                actionLinkNode = null;
            } else {
                switch (notification.source) {
                    case SHARE_PAGE_NOTIFICATION.RESIZE:
                        actionLinkNode = (
                            <a href={Urls.DASHBOARD}>
                                <span> Go to dashboard.</span>
                            </a>
                        );
                        break;
                    case SHARE_PAGE_NOTIFICATION.TRANSLATION:
                        actionLinkNode = (
                            <span>
                                {' '}
                                You can{' '}
                                <a href={Urls.APP + '?id=' + notification.translated_video_id}>
                                    click here to edit
                                </a>{' '}
                                the video.
                            </span>
                        );
                        break;
                }
            }

            return (
                <Callout
                    key={index}
                    className="mx-l5-16"
                    msgType={isError ? CALLOUT_TYPES.ERROR : CALLOUT_TYPES.INFO}
                    text={
                        <>
                            {notification.msg} {actionLinkNode}
                        </>
                    }
                />
            );
        });
    }

    const tabItems: React.ReactElement[] = [];
    const canCreateVideos = SERVER.USER && roleCanCreateVideos(SERVER.USER.role);
    if (showControls) {
        tabItems.push(
            <Tab
                key="create"
                eventKey="create"
                title={canCreateVideos ? 'Create' : 'Duplicate'}
                className={`mx-l5-24 ${styles['share-video__create']}`}
            >
                <>
                    {canCreateVideos && (
                        <>
                            <div className="d-flex flex-column">
                                <h6 className="mb-l5-24 font-l5 font-l5--h6">Create...</h6>
                                <SecondaryButton
                                    className="mb-l5-16"
                                    iconLeft="add"
                                    text="New video"
                                    onClick={onCreateNewVideo}
                                />
                                <>
                                    <TertiaryButton
                                        iconLeft="auto_awesome"
                                        text="Video spinoffs"
                                        onClick={() =>
                                            window.open(
                                                `${Urls.SKETCH}?from_video=${video.get('id')}`,
                                                '_blank',
                                            )
                                        }
                                    />
                                    <div
                                        className={`d-flex w-100 justify-content-center mt-l5-8 font-l5 font-l5--subtext color-grey-800 ${styles['share-video__create__blurb']}`}
                                    >
                                        Create additional videos from this video to expand
                                        your&nbsp;reach
                                    </div>
                                </>
                            </div>
                            <hr className="my-l5-32 border" />
                        </>
                    )}
                    <h6 className="mb-l5-24 font-l5 font-l5--h6">Duplicate and...</h6>
                    <div className="d-flex mb-l5-24 align-items-center">
                        <TertiaryButton
                            data-testid="videoTranslate"
                            text="Translate"
                            iconLeft="g_translate"
                            className={`me-l5-16 ${styles['fixed-width-btn']}`}
                            onClick={translateVideoClicked}
                            size={BUTTON_SIZES.SM}
                            disabled={!showControls}
                        />
                        <span className="font-l5 font-l5--subtext">
                            Create a new version of this video in another language.
                        </span>
                    </div>
                    <div className="d-flex align-items-center">
                        <AutoResize
                            videoAspectRatio={aspectRatio}
                            notifyUser={addNotification}
                            videoId={video.get('id')}
                            className={`me-l5-16 ${styles['fixed-width-btn']}`}
                        />
                        <span className="font-l5 font-l5--subtext">
                            Create a new version of this video in a different format.
                        </span>
                    </div>
                </>
            </Tab>,
        );
    }
    tabItems.push(
        <Tab key="comment" eventKey="comment" title="Comment" className="mx-l5-24">
            {currentSceneIdForComments && (
                <>
                    <h6>Scene {getSceneIndexById(currentSceneIdForComments)}</h6>
                    <div className="flex-grow-1 overflow-y-auto">
                        <VideoCommentList
                            isFluid
                            video_id={video.get('id')}
                            videoCreationSource={video.get('creation_source')}
                            parentVideoLanguage={video.get('edit_parent_language')}
                            slideId={currentSceneIdForComments}
                            mentionableUsers={mentionableUsers}
                            videoOwner={videoOwner}
                            onBeforeCommenting={SERVER.USER ? undefined : handleOnBeforeCommenting}
                            showCommentButtons
                            onCommentInputFocus={() => {
                                setForceVideoToPause(true);
                            }}
                        />
                    </div>
                    {!SERVER.USER && (
                        <AnonymousUserModal
                            showLoginFirstModal={showLoginFirstModal}
                            hideAnonymousUserModal={hideAnonymousUserModal}
                            addAnonymousComment={addAnonymousComment}
                        />
                    )}
                </>
            )}
        </Tab>,
    );

    return (
        <>
            <div className={`${styles['share-video-tab-panel']} overflow-y-auto col-12 col-xl-9`}>
                <div className="flex-grow-1 px-l5-16 py-l5-48">
                    <div className={`${styles['video-preview-column']} mx-l5-auto`}>
                        {isVideoL5Branded && <UpgradeToRemoveCreditSceneCallout />}
                        {renderNotification}
                        <div className="card">
                            <div className={styles['video-preview-section']}>
                                {videoPreviewNode}
                            </div>
                            <hr className="my-l5-0" />
                            <div className="row">
                                <div className="col-sm-12">
                                    {showControls ? (
                                        <h4 className="mx-l5-24 my-l5-24 force-height overflow-hidden line-clamp">
                                            <TextInput
                                                variant={TEXT_INPUT_VARIANTS.UNDERLINED}
                                                text={video.get('name')}
                                                onSubmit={updateVideoName}
                                                checkCharLimit={true}
                                                charLimit={VideoTitleParams.MAX_LENGTH}
                                                trackCharLimitEvent={
                                                    sendVideoTitleExceedsCharLimitEvent
                                                }
                                                submitOnBlur
                                            />
                                        </h4>
                                    ) : (
                                        <h4 className="mx-l5-24 my-l5-24">{video.get('name')}</h4>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-6">
                                    <UserInformationDisplay
                                        user={video.get('last_updated_by')}
                                        className="mx-l5-24"
                                        bioLine={
                                            (video.get('status') === VideoStatus.PUBLISHED
                                                ? 'Published on '
                                                : 'Updated on ') + lastUpdatedDate
                                        }
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <div className="d-flex justify-content-end mx-l5-24">
                                        {editButton}
                                        {downloadButton}
                                    </div>
                                </div>
                            </div>
                            <hr className="my-l5-24" />
                            <div className="ms-l5-24 font-l5 font-l5--subtext color-grey-600">
                                ACTIONS
                            </div>
                            <div className="d-flex align-items-center flex-row flex-wrap">
                                <div className="d-flex flex-wrap mb-l5-24">
                                    <div className="ms-l5-24">{visibilitySelect}</div>
                                    <SimpleButton
                                        variant={BUTTON_VARIANTS.LINK}
                                        text="Copy link"
                                        iconLeft="link"
                                        className={copyLinkButtonClass}
                                        onClick={copyLinkClicked}
                                    />
                                    {downloadDropdownItems.size > 0 && (
                                        <Dropdown
                                            toggleProps={downloadDropdownToggleProps}
                                            itemPropsList={downloadDropdownItems}
                                            onSelect={onDownloadDropdownSelect}
                                            drop="up"
                                            className="ms-l5-4 d-inline-flex align-items-center"
                                        />
                                    )}
                                    <SimpleButton
                                        text="View transcript"
                                        iconLeft="description"
                                        iconLeftVariant={ICON_VARIANTS.OUTLINE}
                                        className="ms-l5-4"
                                        onClick={toggleTranscriptModal}
                                    />
                                    {isWebShareSupported && videoDownloadUrl && (
                                        <SimpleButton
                                            text={isShareFetching ? 'Sharing...' : 'Share to...'}
                                            iconLeft={isShareFetching ? SPINNER_ICON : 'share'}
                                            iconLeftVariant={ICON_VARIANTS.OUTLINE}
                                            className="ms-l5-8"
                                            onClick={handleWebShare}
                                            disabled={isShareFetching}
                                        />
                                    )}
                                </div>
                            </div>
                            {showControls && (
                                <YouTubeChannelCallout hasDownloaded={!!videoDownloadUrl} />
                            )}
                            {notificationNodes}
                            {mediaSourcesDetails}
                            <TranscriptModal
                                showing={showingTranscriptModal}
                                onHidden={toggleTranscriptModal}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={classNames(
                    styles['share-video-tab-panel'],
                    styles['create-tab'],
                    'overflow-y-auto col-12 col-xl-3',
                )}
            >
                <Tabs
                    className="mx-l5-24 mt-l5-24 mb-l5-32"
                    titlePosition={TAB_TITLE_POSITION.START}
                    activeKey={activeTabKey}
                    onSelect={onTabSelect}
                >
                    {tabItems}
                </Tabs>
            </div>
        </>
    );
});

type ShareVideoPropsFromRedux = ConnectedProps<typeof connector>;

/**
 * NOTE currently this component only supports videos with a single video render
 */
const ShareVideo = React.memo(function ShareVideo({
    isVideoL5Branded,
    sceneList,
    idsByStartTime,
    aspectRatio,
    clearAndLoadLuminary,
    videoHasVoiceDrivenSceneGroups,
    srtDownload,
}: ShareVideoPropsFromRedux & NoOwnProps) {
    const dispatch = useAppDispatch();
    const video = useSelector((state: RootState) => state.shareVideo.video);
    const videoId = video.get('id');
    const loading = useSelector((state: RootState) => state.shareVideo.loading);
    const renderEstimate = useSelector((state: RootState) => state.shareVideo.renderEstimate);
    const _isVideoRendering = useSelector(isVideoRendering);
    const visibility = useSelector(selectVideoVisibility);
    const commenterId = SERVER.USER ? SERVER.USER.id : null;
    const showControls = useSelector(canSeeVideoControls);
    const isVideoLimitExceeded = useSelector(isVideoLimitExceededInCycle);
    const _updateVideoVisibility = useCallback(
        (visibility: VideoVisibility) => dispatch(updateVideoVisibility(visibility, videoId)),
        [dispatch, videoId],
    );

    const _updateVideoName = useCallback(
        (name: string) => {
            if (name.trim().length <= 0) {
                name = VideoTitleDefaults.DEFAULT_TITLE;
            }
            ApiService.videoPartialUpdate({
                path: { video_id: videoId.toString() },
                body: { name },
            })
                .then(() => {
                    dispatch(setVideoName(name));
                })
                .catch(() => {
                    Toast.error('Failed to update video name. Please try again.');
                });
        },
        [dispatch, videoId],
    );

    const onCreateNewVideo = useCallback(() => {
        if (isVideoLimitExceeded) {
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.FEATURE_LOCK,
                    modal_context: ImmutableFromJS({
                        featureName: LOCKED_FEATURES.VIDEO_LIMIT,
                    }),
                }),
            );
            return;
        }
        analytics.track('Share page create new video');

        Navigation.openNewTab(Urls.APP);
    }, [dispatch, isVideoLimitExceeded]);

    /* video approval status related */
    const videoApprovalStatus = video.get('video_approval_status');
    const canDownload = useDownloadVideoPermissions(videoApprovalStatus);

    /* commenting-related */
    const deepLinkSceneId = QueryString.scene_id;
    const [currentSceneIdForComments, setCurrentSceneIdForComments] = useState<string | null>(null);
    const [jumpToSceneWithCommentsOnce, setJumpToSceneWithCommentsOnce] = useState(false);
    const numOpenCommentsPerScene = useSelector(getNumOpenCommentsPerScene);
    const commentsAreLoading = useSelector(getCommentsAreLoading);
    const commentMarkerCallback = (startTime: number, forceCommentingMode = false) => {
        const sceneId = idsByStartTime.get(startTime);
        setCurrentSceneIdForComments(sceneId ?? null);
    };

    const videoMembers = useSelector((state: RootState) =>
        selectUsersWithAccessToWorkspaceAndProject(
            state,
            selectVideoWorkspaceId(state),
            selectVideoProjectId(state),
        ),
    );
    const usersFromComments = useSelector(getUsersFromCommentThread);
    const videoOwner = video.get('user_id');

    let mentionableUsers = Immutable.List<ImmutableMap<CommentablePersonProfile>>();
    const isCommenterTeamMember = videoMembers.some((userObj) => userObj.get('id') === commenterId);
    if (isCommenterTeamMember) {
        mentionableUsers = videoMembers.map(convertUserToCommentablePersonProfile);
        // de-dupe by emails to get final list of team members + users who have left a comment in thread
        const dedupedUsers = usersFromComments.filter(
            (userFromComments) =>
                !mentionableUsers.findEntry(
                    (user) => user.get('email') === userFromComments.get('email'),
                ),
        );
        mentionableUsers = mentionableUsers.concat(dedupedUsers);
    }

    /* anonymous user related */
    const showLoginFirstModal = useSelector(isLoginRequiredShowing);
    const showAnonymousUserModal = () =>
        dispatch(showModal({ modal_id: MODAL_IDS.LOGIN_REQUIRED_FOR_COMMENTING }));
    const hideAnonymousUserModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    /* video information */
    const mediaSourceInfo = useLuminarySelector(getVideoEditableMediaAssetSourceData, []);

    /* video translation */
    const showTranslationUpsellModal = () =>
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: ImmutableFromJS({
                    featureName: LOCKED_FEATURES.TRANSLATION,
                }),
            }),
        );

    // polls for video status if video status is processing or queued
    useLoadRenderDetailsAndPoll();

    useEffect(() => {
        // we need to fetch projects onload and only do this once
        if (videoId) {
            dispatch(loadVideoComments(videoId));
            dispatch(loadVideoTranslations(videoId));
            if (SERVER.USER) {
                dispatch(loadTeamSettings());
                dispatch(loadTeamMembers());
            }
        }
    }, [videoId, video, dispatch]);

    useEffect(() => {
        // on initial load, when comments have been loaded, the comments pane should
        // load all the comments for the first scene that has comments
        // ie. if scene 2 is the first scene with comments, load the comments for scene 2 first even though
        //     scene 1 is the first scene
        if (!jumpToSceneWithCommentsOnce && idsByStartTime.size > 0 && !commentsAreLoading) {
            // if there's a deep link to the scene id, show the comments for that scene instead
            if (deepLinkSceneId) {
                setCurrentSceneIdForComments(deepLinkSceneId);
                setJumpToSceneWithCommentsOnce(true);
            } else {
                let firstSceneWithComments = idsByStartTime.get(0); // defaults to first scene if no comments
                idsByStartTime.entrySeq().every(([startTime, sceneId]: [number, string]) => {
                    if (numOpenCommentsPerScene.has(sceneId)) {
                        firstSceneWithComments = idsByStartTime.get(startTime);
                        return false; // exit loop early, return first match
                    }
                    return true;
                });
                setCurrentSceneIdForComments(firstSceneWithComments ?? null); // default to first scene
                setJumpToSceneWithCommentsOnce(true);
            }
        }
    }, [
        idsByStartTime,
        numOpenCommentsPerScene,
        jumpToSceneWithCommentsOnce,
        commentsAreLoading,
        deepLinkSceneId,
    ]);

    // Initial video load
    useEffect(() => {
        dispatch(loadVideo());
    }, [dispatch]);

    useEffect(() => {
        if (video.size) {
            clearAndLoadLuminary(video.get('video_data').toJS());
        }
    }, [clearAndLoadLuminary, video]);

    useEffect(() => {
        dispatch(loadSupportedLanguages());
        dispatch(loadTemplates());
    }, [dispatch]);

    return (
        <div
            className={classNames(
                styles['share-video'],
                styles['with-comments'],
                'd-flex h-100 overflow-y-auto',
            )}
            data-testid="share-video"
        >
            <ShareVideoPresentation
                video={video}
                loading={loading}
                visibility={visibility}
                renderEstimate={renderEstimate}
                isVideoRendering={_isVideoRendering}
                updateVideoVisibility={_updateVideoVisibility}
                updateVideoName={_updateVideoName}
                showControls={showControls}
                aspectRatio={aspectRatio}
                isVideoL5Branded={isVideoL5Branded}
                commentMarkerCallback={commentMarkerCallback}
                currentSceneIdForComments={currentSceneIdForComments}
                sceneList={sceneList}
                numOpenCommentsPerScene={numOpenCommentsPerScene}
                mentionableUsers={mentionableUsers}
                videoOwner={videoOwner}
                showLoginFirstModal={showLoginFirstModal}
                showAnonymousUserModal={showAnonymousUserModal}
                hideAnonymousUserModal={hideAnonymousUserModal}
                canDownload={canDownload}
                onCreateNewVideo={onCreateNewVideo}
                videoHasVoiceDrivenSceneGroups={videoHasVoiceDrivenSceneGroups}
                srtDownload={srtDownload}
                mediaSourceInfo={mediaSourceInfo}
                showTranslationUpsellModal={showTranslationUpsellModal}
            />
        </div>
    );
});

const mapLuminaryStateToProps = (luminary: Luminary, ownProps: NoOwnProps, cacheRef: CacheRef) => ({
    isVideoL5Branded: isVideoL5Branded(luminary),
    sceneList: cacheRef(
        'sceneList',
        luminary.sceneGroups
            .flatMap((sceneGroup) => sceneGroup.scenes)
            .map((scene) => ({ id: scene.id, startTime: scene.startTime })),
    ),
    videoHasVoiceDrivenSceneGroups: luminary.sceneGroups.some((sceneGroup) =>
        isVoiceDrivenSceneGroup(sceneGroup),
    ),
    idsByStartTime: cacheRef('idsByStartTime', slideIdsByStartTime(luminary)),
    aspectRatio: luminary.aspectRatio,
    duration: luminary.duration,
});
const mapLuminaryOperationsToProps = (
    dispatch: AppDispatch,
    getState: GetRootState,
    luminary: Luminary,
) => {
    return {
        clearAndLoadLuminary: async (videoData: OpaqueVideoData) => {
            await luminary.clear();
            luminary.load(videoData);
            await luminary.onCanPlay();
            await luminary.startDrawingToCanvas();
        },
        ...bindLuminaryOperations(
            {
                srtDownloadOperation,
            },
            dispatch,
            getState,
            luminary,
        ),
    };
};

const connector = luminaryReduxConnect(
    undefined,
    undefined,
    mapLuminaryStateToProps,
    mapLuminaryOperationsToProps,
);

const ShareVideoConnected = connector(ShareVideo);

export default ShareVideoConnected;
